import React from 'react';
import useHttp from '../../../hooks/http.hook';
import './ProductStatsList.css';

function ProductStatsList({ branchKey, periodId, active = false }) {
  const { requestWithMeta } = useHttp();
  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [fetchError, setFetchError] = React.useState(false);
  const [retryTick, setRetryTick] = React.useState(0);

  const branch = branchKey || 'store';
  const period = Number(periodId) || 1;

  React.useEffect(() => {
    if (!active) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setFetchError(false);
      try {
        const res = await requestWithMeta(
          `/operator/stats/products/branch/${encodeURIComponent(branch)}?periodId=${period}`,
          'GET'
        );
        if (cancelled) return;
        if (!res?.ok) {
          setItems([]);
          setFetchError(true);
          return;
        }
        const raw = res?.data?.stats;
        const list = Array.isArray(raw) ? raw : [];
        const normalized = list
          .map((row) => {
            const name = String(row?.name ?? '').trim();
            const quantity = Number(row?.quantity ?? 0);
            return {
              name,
              quantity: Number.isFinite(quantity) ? quantity : 0,
            };
          })
          .filter((row) => row.name.length > 0);
        setItems(normalized);
        setFetchError(false);
      } catch (_) {
        if (!cancelled) {
          setItems([]);
          setFetchError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, branch, period, requestWithMeta, retryTick]);

  const maxQty = React.useMemo(() => {
    let m = 0;
    for (const row of items) {
      if (row.quantity > m) m = row.quantity;
    }
    return m;
  }, [items]);

  const totalQty = React.useMemo(
    () => items.reduce((sum, row) => sum + row.quantity, 0),
    [items]
  );

  const nf = React.useMemo(() => new Intl.NumberFormat('ru-RU'), []);
  const nfShare = React.useMemo(
    () => new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    []
  );

  const onRetry = () => {
    setRetryTick((t) => t + 1);
  };

  if (!active) return null;

  return (
    <section
      id="analytics-section-products"
      className="product-stats"
      aria-label="Продажи по товарам"
    >
      <div className="product-stats-panel">
          {loading ? (
            <ul className="product-stats-list" aria-busy="true">
              {[0, 1, 2, 3].map((i) => (
                <li key={i} className="product-stats-row product-stats-row--skeleton">
                  <div className="product-stats-rowTop">
                    <div className="product-stats-skel product-stats-skel--name" />
                    <div className="product-stats-skel product-stats-skel--qty" />
                  </div>
                  <div className="product-stats-barTrack">
                    <div className="product-stats-barFill product-stats-barFill--skeleton" />
                  </div>
                </li>
              ))}
            </ul>
          ) : fetchError ? (
            <div className="product-stats-fallback">
              <p className="product-stats-fallbackText">Не удалось загрузить данные.</p>
              <button type="button" className="product-stats-retry" onClick={onRetry}>
                Повторить
              </button>
            </div>
          ) : items.length === 0 ? (
            <p className="product-stats-empty">Нет продаж за выбранный период.</p>
          ) : (
            <ul className="product-stats-list">
              {items.map((row, idx) => {
                const pct = maxQty > 0 ? Math.round((row.quantity / maxQty) * 100) : 0;
                const share = totalQty > 0 ? (row.quantity / totalQty) * 100 : 0;
                const label = nf.format(row.quantity);
                const rank = idx + 1;
                const rankClass =
                  rank === 1 ? ' product-stats-rank--1' : rank === 2 ? ' product-stats-rank--2' : rank === 3 ? ' product-stats-rank--3' : '';
                return (
                  <li key={`${row.name}:${idx}`} className="product-stats-row">
                    <div className="product-stats-rowTop">
                      <span className={`product-stats-rank${rankClass}`}>{rank}</span>
                      <span className="product-stats-name" title={row.name}>{row.name}</span>
                      <span className="product-stats-qty" title={`Продано: ${label}`}>
                        {label}
                      </span>
                    </div>
                    <div
                      className="product-stats-barTrack"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={maxQty}
                      aria-valuenow={row.quantity}
                      aria-label={`${row.name}: ${label} из максимума ${nf.format(maxQty)}`}
                    >
                      <div
                        className="product-stats-barFill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="product-stats-share">{nfShare.format(share)}% от всех продаж</div>
                  </li>
                );
              })}
            </ul>
          )}
      </div>
    </section>
  );
}

export default ProductStatsList;
